import React, { useMemo } from 'react'
import { Select } from '@/components/Select'
import { useUIVersion } from '@/utils/UIVersionContext'
import { CompositeFilterDescriptor } from '@progress/kendo-data-query/dist/npm/filtering/filter-descriptor.interface'
import { FilterType } from 'components/Filter'
import { useAppSelector } from 'store'

const useMapStateToProps = () => ({
  filters: useAppSelector((state) => state.identity.filter),
})

export interface SelectFilterEvent {
  filterId: string
  filter: CompositeFilterDescriptor
}

interface SelectFiltersProps {
  filterName?: string
  filterType: FilterType
  onChange?: ({ filterId, filter }: SelectFilterEvent) => void
}

const useFilter = (props: { filterType?: FilterType }) => {
  const { filters } = useMapStateToProps()

  return { filters: filters ?? [], filterType: props.filterType }
}

export const SelectFilters: React.FC<SelectFiltersProps> = (props: SelectFiltersProps) => {
  const { isNewUI } = useUIVersion()
  const { filters, filterType } = useFilter({ filterType: props.filterType })
  const availableFilters = useMemo(
    () => filters.filter((filter) => filter.filterType.id === filterType),
    [filterType, filters]
  )
  const hasSavedFilters = availableFilters.length > 0
  const selectedFilterId = availableFilters.some((filter) => filter.id === props.filterName)
    ? props.filterName
    : undefined

  const handleChange = (filterId?: string | number) => {
    if (!filterId) {
      return
    }

    const normalizedFilterId = String(filterId)
    const filter = availableFilters.find((x) => x.id === normalizedFilterId)

    if (filter?.data && props?.onChange) {
      props.onChange({
        filterId: normalizedFilterId,
        filter: JSON.parse(filter.data),
      })
    }
  }

  return (
    <Select
      value={selectedFilterId}
      disabled={!hasSavedFilters}
      size={isNewUI ? 'lg' : 'sm'}
      options={availableFilters.map((filter) => ({
        value: filter.id,
        label: filter.name,
        text: filter.name,
      }))}
      placeholder={hasSavedFilters ? 'Select filter' : 'No Saved Filters'}
      onChange={handleChange}
      className='max-w-38 w-full'
      data-test='filter-search-field'
    />
  )
}
